import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'primary', size = 'md', icon, children, style, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center gap-2 rounded-full font-medium transition-colors';

    const variants = {
      primary: 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary',
      secondary: 'bg-secondary text-white dark:bg-secondary/80',
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      outline:
        'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
    };

    const sizes = {
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm',
    };

    const sizeStyles = {
      sm: {
        paddingLeft: '0.5rem', // px-2 = 0.5rem = 8px
        paddingRight: '0.5rem',
        paddingTop: '0.125rem', // py-0.5 = 0.125rem = 2px
        paddingBottom: '0.125rem',
      },
      md: {
        paddingLeft: '0.625rem', // px-2.5 = 0.625rem = 10px
        paddingRight: '0.625rem',
        paddingTop: '0.25rem', // py-1 = 0.25rem = 4px
        paddingBottom: '0.25rem',
      },
      lg: {
        paddingLeft: '0.75rem', // px-3 = 0.75rem = 12px
        paddingRight: '0.75rem',
        paddingTop: '0.375rem', // py-1.5 = 0.375rem = 6px
        paddingBottom: '0.375rem',
      },
    };

    return (
      <span
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        style={{ ...sizeStyles[size], ...style }}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';
